export interface BulkRunResult<T> {
    succeeded: T[];
    failed: { item: T; error: unknown }[];
    cancelled: boolean;
}

const CANCEL_POLL_MS = 250;

/** Waits `ms`, but wakes up early (in CANCEL_POLL_MS steps) if shouldCancel() flips true. */
async function interruptibleDelay(ms: number, shouldCancel?: () => boolean): Promise<void> {
    if (!shouldCancel) {
        await new Promise((resolve) => setTimeout(resolve, ms));
        return;
    }
    let waited = 0;
    while (waited < ms) {
        if (shouldCancel()) return;
        const step = Math.min(CANCEL_POLL_MS, ms - waited);
        await new Promise((resolve) => setTimeout(resolve, step));
        waited += step;
    }
}

/**
 * Runs an async task over many items with a fixed concurrency limit — a worker-pool, not
 * Promise.all (fires everything at once, can overwhelm an external service like SMTP or
 * LetterXpress) and not a sequential for-loop (safe but slow with no visible progress).
 */
export async function runBulkThrottled<T>(
    items: T[],
    concurrency: number,
    task: (item: T) => Promise<unknown>,
    onProgress?: (done: number, total: number) => void,
): Promise<BulkRunResult<T>> {
    const succeeded: T[] = [];
    const failed: { item: T; error: unknown }[] = [];
    let done = 0;
    let nextIndex = 0;

    async function worker() {
        while (nextIndex < items.length) {
            const item = items[nextIndex++];
            try {
                await task(item);
                succeeded.push(item);
            } catch (error) {
                failed.push({ item, error });
            } finally {
                done++;
                onProgress?.(done, items.length);
            }
        }
    }

    const workerCount = Math.max(1, Math.min(concurrency, items.length));
    await Promise.all(Array.from({ length: workerCount }, worker));

    return { succeeded, failed, cancelled: false };
}

/**
 * Runs an async task over many items in fixed-size batches, pausing between batches. Unlike
 * runBulkThrottled (a continuous worker-pool — as soon as one slot frees up, the next item
 * starts immediately), this gives an external service actual wall-clock time to release prior
 * connections between bursts. Needed for things like SMTP providers that reject with "too many
 * connections" even at low concurrency, because a just-closed connection isn't necessarily torn
 * down on the server's side yet when the next one opens.
 */
export async function runBulkInBatches<T>(
    items: T[],
    batchSize: number,
    delayMs: number,
    task: (item: T) => Promise<unknown>,
    onProgress?: (done: number, total: number) => void,
    shouldCancel?: () => boolean,
): Promise<BulkRunResult<T>> {
    const succeeded: T[] = [];
    const failed: { item: T; error: unknown }[] = [];
    let done = 0;
    let cancelled = false;

    for (let i = 0; i < items.length; i += batchSize) {
        if (shouldCancel?.()) { cancelled = true; break; }

        const batch = items.slice(i, i + batchSize);
        // A cancellation mid-batch can't un-send requests already in flight, so a batch
        // that started always runs to completion — failures within it never halt the loop
        // either, only an explicit cancel (checked before the *next* batch) does.
        const results = await Promise.allSettled(batch.map((item) => task(item)));
        results.forEach((result, idx) => {
            const item = batch[idx];
            if (result.status === "fulfilled") succeeded.push(item);
            else failed.push({ item, error: result.reason });
            done++;
            onProgress?.(done, items.length);
        });

        const isLastBatch = i + batchSize >= items.length;
        if (!isLastBatch && delayMs > 0) {
            await interruptibleDelay(delayMs, shouldCancel);
            if (shouldCancel?.()) { cancelled = true; break; }
        }
    }

    return { succeeded, failed, cancelled };
}
